import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertCircle, KeyRound, Loader2 } from 'lucide-react';

/**
 * ResetPasswordPage — handles the link from the password-reset email.
 * URL format: /reset-password?token=<raw-token>
 *
 * Posts to POST /api/auth/reset-password with { token, new_password }.
 * Shows a clean Dutch form, error/success states, and a CTA back to
 * /admin/login on success. No raw token is ever rendered or logged.
 */
export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token || token.length < 16) {
      setError('Resetlink is ongeldig of incompleet.');
    }
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8) {
      setError('Wachtwoord moet minimaal 8 tekens zijn.');
      return;
    }
    if (pw !== pw2) {
      setError('De twee wachtwoorden komen niet overeen.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        '/api/auth/reset-password',
        { token, new_password: pw },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (data?.success) setDone(true);
      else setError('Resetlink is ongeldig of al gebruikt.');
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Resetlink is ongeldig of verlopen. Vraag een nieuwe aan.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-muted/30 px-4"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1
              className="text-xl font-semibold text-foreground"
              style={{ fontFamily: 'Outfit' }}
              data-testid="reset-password-title"
            >
              Wachtwoord opnieuw instellen
            </h1>
            <p className="text-xs text-muted-foreground">Powersure account</p>
          </div>
        </div>

        {done ? (
          <div className="space-y-4" data-testid="reset-password-success">
            <div className="flex items-start gap-2 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900">
              <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <strong>Uw wachtwoord is opnieuw ingesteld.</strong>
                <p className="mt-1">U kunt nu inloggen met uw nieuwe wachtwoord.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/admin/login')}
              className="btn-primary w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold"
              data-testid="reset-password-go-to-login"
            >
              Naar inloggen
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
            <div>
              <label htmlFor="pw" className="text-xs font-medium text-foreground uppercase tracking-wider">
                Nieuw wachtwoord
              </label>
              <input
                id="pw"
                type="password"
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                minLength={8}
                required
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="reset-password-input-new"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Minimaal 8 tekens.</p>
            </div>
            <div>
              <label htmlFor="pw2" className="text-xs font-medium text-foreground uppercase tracking-wider">
                Wachtwoord bevestigen
              </label>
              <input
                id="pw2"
                type="password"
                autoComplete="new-password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                minLength={8}
                required
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="reset-password-input-confirm"
              />
            </div>

            {error && (
              <div
                className="flex items-start gap-2 p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-900"
                data-testid="reset-password-error"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !token}
              className="btn-primary w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold disabled:opacity-50"
              data-testid="reset-password-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? 'Bezig…' : 'Wachtwoord opslaan'}
            </button>

            <p className="text-[11px] text-muted-foreground text-center pt-2">
              Werkt de link niet? Vraag een nieuwe resetlink aan via uw beheerder.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
